import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Mic, MicOff, History, Play, Pause, Circle, Square, Loader2 } from 'lucide-react';
import { useSpeechToText } from '../../hooks/useSpeechToText';
import { safeFormatRelativeTime } from '../../utils/safeFormatRelativeTime';
import ProjectStandupHistoryPopover from './ProjectStandupHistoryPopover';

export type StandupNoteVariant = 'project' | 'architect';

const MAX_CHARS = 240;

export interface ProjectStandupLatest {
  text?: string;
  audioUrl?: string;
  updatedAt?: Date;
  updatedByName?: string;
}

export interface ProjectStandupCellProps {
  projectId: string;
  projectLabel?: string;
  latest: ProjectStandupLatest;
  currentUserId: string;
  currentUserName: string;
  onSaved?: () => void;
  variant?: StandupNoteVariant;
  readOnly?: boolean;
  placeholder?: string;
  historyTitle?: string;
}

function pickAudioMimeType(): string {
  if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) return 'audio/webm;codecs=opus';
  if (MediaRecorder.isTypeSupported('audio/webm')) return 'audio/webm';
  if (MediaRecorder.isTypeSupported('audio/mp4')) return 'audio/mp4';
  return 'audio/webm';
}

function InlineAudioButton({ url }: { url: string }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);

  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
      setPlaying(false);
    } else {
      void el.play();
      setPlaying(true);
    }
  };

  return (
    <>
      <audio
        ref={audioRef}
        src={url}
        preload="none"
        onEnded={() => setPlaying(false)}
        onPause={() => setPlaying(false)}
      />
      <button
        type="button"
        className="pm-standup-audio-btn"
        onClick={toggle}
        title={playing ? 'Pause' : 'Play voice note'}
        aria-label={playing ? 'Pause voice note' : 'Play voice note'}
      >
        {playing ? <Pause size={11} /> : <Play size={11} />}
      </button>
    </>
  );
}

const ProjectStandupCell: React.FC<ProjectStandupCellProps> = ({
  projectId,
  projectLabel,
  latest,
  currentUserId,
  currentUserName,
  onSaved,
  variant = 'project',
  readOnly = false,
  placeholder = 'Drop a quick update…',
  historyTitle,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const closeHistory = useCallback(() => setHistoryOpen(false), []);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordError, setRecordError] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const historyBtnRef = useRef<HTMLButtonElement>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const saveTimerRef = useRef<number | null>(null);
  const baselineRef = useRef('');

  const appendTranscript = useCallback((chunk: string) => {
    setDraft(prev => {
      const combined = prev ? `${prev} ${chunk}` : chunk;
      return combined.slice(0, MAX_CHARS);
    });
  }, []);

  const speech = useSpeechToText({ onFinalChunk: appendTranscript });

  useEffect(() => {
    if (editing) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [editing]);

  const startEditing = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    if (readOnly) return;
    const existing = typeof latest.text === 'string' ? latest.text : '';
    baselineRef.current = existing;
    setDraft(existing);
    setAudioBlob(null);
    setRecordError(null);
    setEditing(true);
  };

  const cancelEditing = useCallback(() => {
    speech.stop();
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop();
    }
    streamRef.current?.getTracks().forEach(t => t.stop());
    setIsRecording(false);
    setAudioBlob(null);
    setDraft(baselineRef.current);
    setEditing(false);
  }, [speech]);

  const commitSave = useCallback(async () => {
    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    speech.stop();
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop();
    }

    const trimmed = draft.trim();
    const existingText = typeof latest.text === 'string' ? latest.text : '';
    const hasAudio = audioBlob && audioBlob.size > 0;
    if (!trimmed && !hasAudio) {
      if (editing && !existingText && !latest.audioUrl) {
        setEditing(false);
      }
      return;
    }

    const unchanged =
      trimmed === existingText.trim()
      && !hasAudio;
    if (unchanged) {
      setEditing(false);
      return;
    }

    setSaving(true);
    try {
      if (variant === 'architect') {
        const { createArchitectStandupNote } = await import('../../utils/architectStandupService');
        const note = await createArchitectStandupNote(
          projectId,
          { text: trimmed, audioBlob },
          { id: currentUserId, displayName: currentUserName },
        );
        baselineRef.current = note.text;
      } else {
        const { createAndApplyProjectStandupNote } = await import('../../utils/projectStandupService');
        const note = await createAndApplyProjectStandupNote(
          projectId,
          { text: trimmed, audioBlob },
          { id: currentUserId, displayName: currentUserName },
        );
        baselineRef.current = note.text;
      }
      setEditing(false);
      setAudioBlob(null);
      onSaved?.();
    } catch (err) {
      console.error('[ProjectStandupCell] save failed', err);
      setRecordError(err instanceof Error ? err.message : 'Could not save standup note.');
    } finally {
      setSaving(false);
    }
  }, [
    audioBlob,
    currentUserId,
    currentUserName,
    draft,
    editing,
    latest.audioUrl,
    latest.text,
    onSaved,
    projectId,
    speech,
    variant,
  ]);

  const scheduleSave = useCallback(() => {
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(() => {
      void commitSave();
    }, 300);
  }, [commitSave]);

  const startRecording = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isRecording) {
      recorderRef.current?.stop();
      return;
    }
    setRecordError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = pickAudioMimeType();
      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      recorder.ondataavailable = ev => {
        if (ev.data.size > 0) chunksRef.current.push(ev.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setAudioBlob(blob);
        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        setIsRecording(false);
      };
      recorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
    } catch {
      setRecordError('Microphone access denied or unavailable.');
    }
  };

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
      speech.stop();
      if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
      streamRef.current?.getTracks().forEach(t => t.stop());
    };
  }, [speech]);

  const noteText = typeof latest.text === 'string' ? latest.text : '';
  const relativeUpdated = safeFormatRelativeTime(latest.updatedAt);
  const metaLine =
    latest.updatedAt && latest.updatedByName
      ? `${latest.updatedByName}${relativeUpdated ? ` · ${relativeUpdated}` : ''}`
      : null;

  if (editing) {
    const displayValue =
      speech.interimTranscript && !draft.endsWith(speech.interimTranscript)
        ? `${draft}${draft ? ' ' : ''}${speech.interimTranscript}`
        : draft;

    return (
      <div className="pm-standup-cell pm-standup-cell--editing" onClick={(e) => e.stopPropagation()}>
        <div className="pm-standup-edit-row">
          <span className="pm-standup-input-wrap">
            <input
              ref={inputRef}
              type="text"
              className="pm-standup-input"
              value={displayValue}
              maxLength={MAX_CHARS}
              placeholder="Waiting on client sign-off…"
              disabled={saving}
              onChange={(e) => setDraft(e.target.value.slice(0, MAX_CHARS))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void commitSave();
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  cancelEditing();
                }
              }}
              onBlur={() => scheduleSave()}
            />
          </span>
        </div>
        <div className="pm-standup-edit-actions">
          {speech.isSupported && (
            <button
              type="button"
              className={`pm-standup-icon-btn${speech.isListening ? ' pm-standup-icon-btn--active' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.stopPropagation();
                speech.toggle();
              }}
              title={speech.isListening ? 'Stop dictation' : 'Dictate'}
              aria-label={speech.isListening ? 'Stop dictation' : 'Start dictation'}
              disabled={saving}
            >
              {speech.isListening ? <MicOff size={13} /> : <Mic size={13} />}
            </button>
          )}
          <button
            type="button"
            className={`pm-standup-icon-btn${isRecording ? ' pm-standup-icon-btn--recording' : ''}${audioBlob ? ' pm-standup-icon-btn--attached' : ''}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={startRecording}
            title={isRecording ? 'Stop recording' : audioBlob ? 'Re-record voice note' : 'Record voice note'}
            aria-label={isRecording ? 'Stop recording' : 'Record voice note'}
            disabled={saving}
          >
            {isRecording ? <Square size={11} fill="currentColor" /> : <Circle size={13} />}
          </button>
          <button
            ref={historyBtnRef}
            type="button"
            className="pm-standup-icon-btn"
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.stopPropagation();
              setHistoryOpen(true);
            }}
            title="View history"
            aria-label="View standup history"
            disabled={saving}
          >
            <History size={13} />
          </button>
          {saving && <Loader2 size={13} className="pm-spin pm-standup-saving" aria-hidden />}
        </div>
        {(audioBlob || recordError || speech.error) && (
          <div className="pm-standup-edit-hints">
            {audioBlob ? <span className="pm-standup-chip">Audio attached</span> : null}
            {isRecording ? <span className="pm-standup-chip pm-standup-chip--rec">Recording…</span> : null}
            {recordError ? <span className="pm-standup-hint-error">{recordError}</span> : null}
            {speech.error ? <span className="pm-standup-hint-error">{speech.error}</span> : null}
          </div>
        )}
        {historyOpen && (
          <ProjectStandupHistoryPopover
            open={historyOpen}
            projectId={projectId}
            projectLabel={projectLabel}
            anchorRef={historyBtnRef}
            onClose={closeHistory}
            variant={variant}
            historyTitle={historyTitle}
          />
        )}
      </div>
    );
  }

  const hoverTitle = [noteText, metaLine].filter(Boolean).join('\n') || placeholder;

  return (
    <div
      className={`pm-standup-cell${noteText || latest.audioUrl ? ' pm-standup-cell--filled' : ''}${readOnly ? ' pm-standup-cell--readonly' : ''}${variant === 'architect' ? ' pm-standup-cell--designer' : ''}`}
      onClick={readOnly ? undefined : startEditing}
      title={hoverTitle}
      role={readOnly ? undefined : 'button'}
      tabIndex={readOnly ? undefined : 0}
      onKeyDown={readOnly ? undefined : (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          startEditing(e);
        }
      }}
    >
      <div className="pm-standup-display">
        {noteText ? (
          <span className="pm-standup-text">{noteText}</span>
        ) : (
          <span className="pm-standup-placeholder">{placeholder}</span>
        )}
        {latest.audioUrl ? <InlineAudioButton url={latest.audioUrl} /> : null}
      </div>
      <button
        ref={historyBtnRef}
        type="button"
        className="pm-standup-history-trigger"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          setHistoryOpen(true);
        }}
        title="View history"
        aria-label="View standup history"
      >
        <History size={11} />
      </button>
      {historyOpen && (
        <ProjectStandupHistoryPopover
          open={historyOpen}
          projectId={projectId}
          projectLabel={projectLabel}
          anchorRef={historyBtnRef}
          onClose={closeHistory}
          variant={variant}
          historyTitle={historyTitle}
        />
      )}
    </div>
  );
};

export default ProjectStandupCell;
